import _mapValues from 'lodash.mapvalues';
import { CONDITIONAL_OPERATORS } from '../../constants';
import { getConditionalExpression, hasThresholdStyling, isCategorizedByStatus } from '../style';
import { isDashedLineLayer, isSolidLineLayer } from './types';

const buildThresholdFilterExpression = (categorizedAttributeLabel, valueInclusionStatusMap) => {
  const allThresholdValues = Object.keys(valueInclusionStatusMap)
    .map((k) => (k === 'Other' ? k : Number(k)))
    .sort((a, b) => {
      if (a === 'Other') return 1;
      if (b === 'Other') return -1;
      return a - b;
    });

  const activeThresholdIndices = allThresholdValues
    .map((v, index) => [v, index])
    .filter(([v]) => valueInclusionStatusMap[v])
    .map(([, index]) => Number(index));

  if (activeThresholdIndices.length < 1) return ['any'];

  const conditions = activeThresholdIndices.flatMap((index) => {
    // Valueless attributes get tossed into the lowest threshold
    if (index === 0) {
      return [
        getConditionalExpression(CONDITIONAL_OPERATORS.EQUALS, categorizedAttributeLabel, null),
        true,
        getConditionalExpression(
          CONDITIONAL_OPERATORS.LT,
          categorizedAttributeLabel,
          allThresholdValues[index],
        ),
        true,
      ];
    }

    // Unlike with standard categorized styles, we cannot simply match against a single
    // value - we must match against any value less than the current threshold, but
    // greater than or equal to it's predecessor.
    if (index < allThresholdValues.length - 1) {
      return [
        [
          'all',
          getConditionalExpression(
            CONDITIONAL_OPERATORS.LT,
            categorizedAttributeLabel,
            allThresholdValues[index],
          ),
          getConditionalExpression(
            CONDITIONAL_OPERATORS.GTE,
            categorizedAttributeLabel,
            allThresholdValues[index - 1],
          ),
        ],
        true,
      ];
    }

    // Any value greater than the top threshold gets categorized as 'Other'
    return [
      getConditionalExpression(
        CONDITIONAL_OPERATORS.GTE,
        categorizedAttributeLabel,
        allThresholdValues[allThresholdValues.length - 2],
      ),
      true,
    ];
  });

  return ['case', ...conditions, false];
};

const buildStandardFilterExpression = (
  layer,
  categorizedAttributeLabel,
  valueInclusionStatusMap,
) => {
  const coercer =
    isCategorizedByStatus(categorizedAttributeLabel) ||
    layer.availableAttributes[categorizedAttributeLabel].htmlInputType === 'number'
      ? Number
      : String;

  const showOther = valueInclusionStatusMap.Other;

  const allAttributeValues = Object.keys(valueInclusionStatusMap)
    .filter((attribute) => attribute !== 'Other')
    .map(coercer);

  const activeAttributeValues = allAttributeValues.filter(
    (attributeValue) => valueInclusionStatusMap[attributeValue],
  );

  const categorizedFilter = [
    'in',
    ['get', categorizedAttributeLabel],
    ['literal', activeAttributeValues],
  ];

  const otherFilter = [
    '!',
    ['in', ['get', categorizedAttributeLabel], ['literal', allAttributeValues]],
  ];

  return showOther ? ['any', categorizedFilter, otherFilter] : ['any', categorizedFilter];
};

export const getCategorizedFilterExpression = (layer, valueInclusionStatusMap) => {
  const { categorizedAttributeLabel } = layer.style;

  return hasThresholdStyling(layer)
    ? buildThresholdFilterExpression(categorizedAttributeLabel, valueInclusionStatusMap)
    : buildStandardFilterExpression(layer, categorizedAttributeLabel, valueInclusionStatusMap);
};

/*
 * to circumvent limitation around expression-driven dash-array styles in mapbox v1
 * (https://github.com/mapbox/mapbox-gl-js/issues/3045) vector vetro line layers have both a.
 * "solid line layer" and a "dashed line layer" the dasharray filter filters out anything that
 * should be dashed from the "solid line layer", and vice versa for "dashed line" layers
 */
export const getDasharrayFilter = (layer, activeStyleLayerId, styleOverride = {}) => {
  if (!isSolidLineLayer(layer) && !isDashedLineLayer(layer)) return null;

  const styleLayer = activeStyleLayerId === layer.id ? { ...layer, ...styleOverride } : layer;

  const { symbols, categorizedAttributeLabel } = styleLayer.style;

  const valueInclusionStatusMap = _mapValues(symbols, (symbol) =>
    Boolean(isDashedLineLayer(styleLayer) ? symbol.strokeDasharray : !symbol.strokeDasharray),
  );

  if (Object.values(valueInclusionStatusMap).every((v) => !v)) {
    // filter out everything
    return ['==', ['get', 'plan_id'], -1];
  }

  if (categorizedAttributeLabel) {
    return getCategorizedFilterExpression(styleLayer, valueInclusionStatusMap);
  }

  return null;
};
